<template>
  <YhcServiceLevelSwitch
    v-if="isPreviewMode"
    :view-mode="viewModeForPreview"
    @update:viewMode="switchViewMode"
  />
  <div class="yhc-service-level">
    <div class="yhc-service-level-header">
      <div class="yhc-service-level-header--logo mb-3">
        <a
          href="https://www.008008.jp/"
          target="_blank"
          rel="noopener"
          ><img src="/images/yhc-logo.jpg"
        /></a>
      </div>
      <YhcServiceLevelNoticeRow
        v-if="isInternalServiceLevelViewActive && currentNoticePageKey"
        class="yhc-service-level__notice"
        :notice-page-key="currentNoticePageKey"
      />
      <YhcServiceLevelNotice
        v-else-if="!isFleaMktViewActive && !isUserViewActive && currentNoticePageKey"
        class="yhc-service-level__notice"
        :notice-page-key="currentNoticePageKey"
      />
      <div class="text-center bold header-title pb-2">
        <span v-if="isFleaMktViewActive">サービスレベル検索(フリマ用)</span>
        <span v-else>サービスレベル検索</span>
      </div>
    </div>
    <!-- 郵便番号入力とお預かり日選択を横並びに配置 -->
    <div class="yhc-service-level-top-section">
      <div class="yhc-service-level-top-section__left">
        <YhcEFZipCodeInput
          title="お預かり先"
          :model-value="$yhcefs.$userInputData?.zip_from"
          :from-or-to="'from'"
          :zip-code-master="zipFromYhcMaster"
          :is-service-level="true"
          @update:modelValue="
            async (updatedValue) => {
              await $yhcefs.$userInputData.updateZip(updatedValue, 'zip_from')
            }
          "
        />
        <YhcEFZipCodeInput
          title="お届け先"
          :model-value="$yhcefs.$userInputData?.zip_to"
          :from-or-to="'to'"
          :zip-code-master="zipToYhcMaster"
          :is-service-level="true"
          @update:modelValue="
            async (updatedValue) => {
              await $yhcefs.$userInputData.updateZip(updatedValue, 'zip_to')
              await $yhcefs.$userInputData.checkDeliveryDateBusinessDayAndResetIfNeeded() // お届け日が営業日でなければクリア
            }
          "
        />
        <!-- 発着選択（お預かり日 or お届け日）※ユーザー用・フリマ用では非表示 -->
        <YhcServiceLevelDateModeSwitch
          v-if="
            hasFromToZip &&
            !deliveryAreaNotAvailable &&
            !isUserViewActive &&
            !isFleaMktViewActive
          "
        />
        <!-- お預かり日選択（発着＝お預かり日のとき、またはフリマ用では表示。ユーザー用では非表示） -->
        <YhcServiceLevelPickupDate
          v-if="
            hasFromToZip &&
            !deliveryAreaNotAvailable &&
            !isUserViewActive &&
            (isFleaMktViewActive ||
              $yhcefs.$userInputData?.serviceLevelDateMode === 'pickup')
          "
        />
        <!-- お届け日選択（発着＝お届け日のときのみ表示、ユーザー用・フリマ用では非表示） -->
        <YhcServiceLevelDeliveryDate
          v-if="
            hasFromToZip &&
            !deliveryAreaNotAvailable &&
            $yhcefs.$userInputData?.serviceLevelDateMode === 'delivery' &&
            !isUserViewActive &&
            !isFleaMktViewActive
          "
        />
      </div>
    </div>
    <!-- お届け可能日は YhcServiceLevelInfo に統合 -->
    <YhcServiceLevelInfo v-if="hasFromToZip" />
    <!-- <YhcServiceLevelExport /> ※25.06.11 表のエクスポートは一旦廃止 -->
  </div>
</template>
<script lang="ts">
import YhcEFZipCodeInput from '../YhcEstimateFormFrontPage/components/ZipCodeInput/YhcEFZipCodeInput.vue'
import YhcServiceLevelInfo from './components/YhcServiceLevelInfo.vue'
import { YhcEstimateFormFrontService } from '../YhcEstimateFormFrontPage/YhcEstimateFormFrontService'
import type { ModelYhcMastersByZipcode } from '../../fireModels/yhcDataTypes'
import { reactive, computed } from 'vue'
import './_yhc-service-level.scss'
import '../YhcEstimateFormFrontPage/components/_yhc-estimate-form-front.scss'
import YhcServiceLevelExport from './components/YhcServiceLevelExport.vue'
// 発着選択・お預かり日選択・お届け日選択コンポーネントをインポート
import YhcServiceLevelDateModeSwitch from './components/YhcServiceLevelDateModeSwitch.vue'
import YhcServiceLevelPickupDate from './components/YhcServiceLevelPickupDate.vue'
import YhcServiceLevelDeliveryDate from './components/YhcServiceLevelDeliveryDate.vue'
// お届け可能日表示コンポーネントをインポート
import YhcServiceLevelDeliveryDates from './components/YhcServiceLevelDeliveryDates.vue'
// コンポーネントをインポート
import YhcServiceLevelSwitch from './YhcServiceLevelSwitch.vue'
// お知らせコンポーネントをインポート
import YhcServiceLevelNotice, {
  sanitizeOptions,
} from './components/YhcServiceLevelNotice.vue'
import YhcServiceLevelNoticeRow from './components/YhcServiceLevelNoticeRow.vue'
// データ取得関数をインポート
import type { FindFilter } from '../../../CORE/src/common/$models'
import {
  findRecordsByModelName,
  SERVICE_LEVEL_NOTICE_HIDDEN_PAGES_DIRECTUS_HINT,
} from '../YhcEstimateFormFrontPage/findRecordsByModelName'
import type { ServiceLevelNoticePageKey } from './serviceLevelNoticePageUtils'
import {
  filterNoticesVisibleOnPage,
  formatServiceLevelNoticeForDisplay,
  type ServiceLevelNoticeRecordForDisplay,
} from './serviceLevelNoticeDisplayUtils'
import type { ServiceLevelRevisionRecord } from './serviceLevelRevisionUtils'

type ModalNoticeRecord = ServiceLevelNoticeRecordForDisplay & {
  display_date: string
  modal_start_datetime: string | null
  modal_end_datetime: string | null
}

export default {
  name: 'YhcServiceLevel',
  components: {
    YhcServiceLevelSwitch,
    YhcEFZipCodeInput,
    YhcServiceLevelInfo,
    YhcServiceLevelExport,
    YhcServiceLevelDateModeSwitch,
    YhcServiceLevelPickupDate,
    YhcServiceLevelDeliveryDate,
    YhcServiceLevelDeliveryDates,
    YhcServiceLevelNotice,
    YhcServiceLevelNoticeRow,
  },
  props: {
    isFleaMarket: {
      type: Boolean,
      default: false,
    },
    isUserView: {
      type: Boolean,
      default: false,
    },
    isCorporationView: {
      type: Boolean,
      default: false,
    },
    isCorporationLeadTimePlusOne: {
      type: Boolean,
      default: false,
    },
    isPreviewMode: {
      type: Boolean,
    },
  },
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance
    },
    zipFromYhcMaster(): ModelYhcMastersByZipcode | null {
      return this.$yhcefs.$userInputData?.zip_from_yhc_master ?? null
    },
    zipToYhcMaster(): ModelYhcMastersByZipcode | null {
      return this.$yhcefs.$userInputData?.zip_to_yhc_master ?? null
    },
    hasFromToZip(): boolean {
      // 郵便番号マスタが存在するかどうか
      return !!(
        this.$yhcefs.$userInputData?.zip_from_yhc_master &&
        this.$yhcefs.$userInputData?.zip_to_yhc_master
      )
    },
    deliveryAreaNotAvailable(): boolean {
      // フリマ用ページの場合は、deliveryAreaFlagが0,2,3のいずれかの場合にtrueを返す
      if (this.isFleaMktViewActive) {
        return (
          this.$yhcefs.$userInputData.zip_from_yhc_master?.deliveryAreaFlag === '0' ||
          this.$yhcefs.$userInputData.zip_from_yhc_master?.deliveryAreaFlag === '2' ||
          this.$yhcefs.$userInputData.zip_from_yhc_master?.deliveryAreaFlag === '3' ||
          this.$yhcefs.$userInputData.zip_to_yhc_master?.deliveryAreaFlag === '0' ||
          this.$yhcefs.$userInputData.zip_to_yhc_master?.deliveryAreaFlag === '2' ||
          this.$yhcefs.$userInputData.zip_to_yhc_master?.deliveryAreaFlag === '3'
        )
      }
      // フリマ用以外は従来の条件
      return (
        this.$yhcefs.$userInputData.zip_from_yhc_master?.deliveryAreaFlag === '3' ||
        this.$yhcefs.$userInputData.zip_to_yhc_master?.deliveryAreaFlag === '3'
      )
    },
    isFleaMktViewActive(): boolean {
      return this.isPreviewMode ? this.viewModeForPreview === 'flea' : this.isFleaMarket
    },
    isUserViewActive(): boolean {
      return this.isPreviewMode ? this.viewModeForPreview === 'user' : this.isUserView
    },
    isCorporationViewActive(): boolean {
      if (this.isPreviewMode) {
        return (
          this.viewModeForPreview === 'corporation' ||
          this.viewModeForPreview === 'corporationLeadPlusOne'
        )
      }
      return this.isCorporationView
    },
    isCorporationLeadTimePlusOneActive(): boolean {
      if (this.isPreviewMode) {
        return this.viewModeForPreview === 'corporationLeadPlusOne'
      }
      return this.isCorporationLeadTimePlusOne
    },
    /** 社内用（質問・要望投稿ボタン表示対象） */
    isInternalServiceLevelViewActive(): boolean {
      return (
        !this.isFleaMktViewActive &&
        !this.isUserViewActive &&
        !this.isCorporationViewActive
      )
    },
    currentNoticePageKey(): ServiceLevelNoticePageKey | null {
      if (this.isInternalServiceLevelViewActive) {
        return 'internal'
      }
      if (this.isCorporationLeadTimePlusOneActive) {
        return 'corporationLeadPlusOne'
      }
      if (this.isCorporationViewActive) {
        return 'corporation'
      }
      return null
    },
    /** サービス改定注釈の表示対象ページか（社内・法人・LT+1・ユーザー用。フリマは非表示） */
    serviceLevelRevisionNotesEnabled(): boolean {
      return !this.isFleaMktViewActive
    },
    /** マスタバージョンまたぎ注釈（社内・法人・LT+1のみ） */
    masterVersionSpanNotesEnabled(): boolean {
      return this.isInternalServiceLevelViewActive || this.isCorporationViewActive
    },
  },
  data() {
    return {
      YhcEstimateFormFrontServiceInstance: reactive(new YhcEstimateFormFrontService()),
      initialized: false,
      viewModeForPreview: 'internal' as
        | 'flea'
        | 'user'
        | 'internal'
        | 'corporation'
        | 'corporationLeadPlusOne',
      serviceLevelRevisions: [] as ServiceLevelRevisionRecord[],
    }
  },
  provide() {
    return {
      YhcEstimateFormFrontServiceInstance: this.YhcEstimateFormFrontServiceInstance,
      serviceLevelRevisions: computed(() => this.serviceLevelRevisions),
      serviceLevelRevisionNotesEnabled: computed(
        () => this.serviceLevelRevisionNotesEnabled,
      ),
      masterVersionSpanNotesEnabled: computed(
        () => this.masterVersionSpanNotesEnabled,
      ),
    }
  },
  async mounted() {
    // init() 内で Handler が作成される前にフラグを設定（法人向けのお預かり日デフォルト算出に必要）
    this.syncServiceLevelViewFlags()
    this.YhcEstimateFormFrontServiceInstance.isServiceLevel = true
    await this.init()
    await this.fetchServiceLevelRevisions()
    // ユーザー用・フリマ用では発着選択を出さず常にお預かり日指定とする
    if (this.isUserViewActive || this.isFleaMktViewActive) {
      this.YhcEstimateFormFrontServiceInstance.$userInputData.serviceLevelDateMode =
        'pickup'
      this.YhcEstimateFormFrontServiceInstance.$userInputData.deliverySelectedDate = null
    }
    // モーダル表示対象のお知らせを表示（ユーザー用・フリマ用では非表示）
    if (!this.isUserViewActive && !this.isFleaMktViewActive) {
      await this.showNoticeModal()
    }
  },
  watch: {
    viewModeForPreview: function (newValue) {
      this.syncServiceLevelViewFlags()
      if (newValue === 'user' || newValue === 'flea') {
        this.YhcEstimateFormFrontServiceInstance.$userInputData.serviceLevelDateMode =
          'pickup'
        this.YhcEstimateFormFrontServiceInstance.$userInputData.deliverySelectedDate =
          null
      }
      if (!this.isUserViewActive && !this.isFleaMktViewActive) {
        void this.showNoticeModal()
      }
    },
  },
  methods: {
    syncServiceLevelViewFlags() {
      const efs = this.YhcEstimateFormFrontServiceInstance
      efs.isFleaMarket = this.isFleaMktViewActive
      efs.isCorporationView = this.isCorporationViewActive
      efs.isCorporationLeadTimePlusOne = this.isCorporationLeadTimePlusOneActive
    },
    async init() {
      this.initialized = false
      await this.YhcEstimateFormFrontServiceInstance.init()
      this.$nextTick(() => {
        this.initialized = true
      })
    },
    async fetchServiceLevelRevisions() {
      try {
        const today = $core.$dayjs().format('YYYY-MM-DD')
        const records = await findRecordsByModelName('yhc_service_level_revisions', {
          filter: {
            revision_date: { _gt: today },
          },
          sort: ['revision_date'],
          limit: -1,
        })
        this.serviceLevelRevisions = (records || []) as ServiceLevelRevisionRecord[]
      } catch (error) {
        console.error('サービス改定の取得に失敗しました:', error)
        console.error(
          '[YHC] Directus の公開(匿名)ロールに yhc_service_level_revisions の読み取り権限を付与したか確認してください。',
        )
        this.serviceLevelRevisions = []
      }
    },
    switchViewMode(
      viewMode:
        | 'flea'
        | 'user'
        | 'internal'
        | 'corporation'
        | 'corporationLeadPlusOne',
    ) {
      this.viewModeForPreview = viewMode
      this.syncServiceLevelViewFlags()
      const userInputData = this.YhcEstimateFormFrontServiceInstance.$userInputData
      if (userInputData && (viewMode === 'user' || viewMode === 'flea')) {
        userInputData.serviceLevelDateMode = 'pickup'
        userInputData.deliverySelectedDate = null
      }
      this.init()
    },
    /**
     * モーダル表示対象のお知らせを取得して表示
     * 複数のお知らせがある場合は、display_date降順で並べて表示し、
     * お知らせ間に横線を引いて区切る
     * ツールチップ対象テキストがある場合はツールチップも表示
     */
    async showNoticeModal() {
      const pageKey = this.currentNoticePageKey
      if (!pageKey) {
        return
      }
      try {
        const now = $core.$dayjs().format('YYYY-MM-DD HH:mm:ss')
        const modalFilterAnd: FindFilter[] = [
          { modal_start_datetime: { _lte: now } },
          { modal_end_datetime: { _gte: now } },
        ]
        const modalNotices = filterNoticesVisibleOnPage(
          (await findRecordsByModelName('yhc_service_level_notices', {
            filter: {
              _and: modalFilterAnd,
            },
            sort: ['-display_date'],
          })) as ModalNoticeRecord[],
          pageKey,
        )

        if (modalNotices.length > 0) {
          const noticesData = modalNotices.map((notice) =>
            formatServiceLevelNoticeForDisplay(notice, sanitizeOptions),
          )

          $core.$modals.openModal({
            modalProps: {
              title: 'お知らせ',
              size: 'md',
              centered: true,
              okOnly: true,
              okTitle: '閉じる',
            },
            component: {
              template: `
                <div class="yhc-notice-modal-content" @click="handleOutsideClick">
                  <div
                    v-for="(notice, index) in notices"
                    :key="notice.id"
                  >
                    <hr v-if="index > 0" class="yhc-notice-modal-separator" />
                    <div
                      class="yhc-notice-modal-item"
                      v-html="notice.contentHtml"
                      @click="handleTooltipToggle($event, notice)"
                    ></div>
                    <ul
                      v-if="notice.attachmentLinks.length"
                      class="yhc-notice-modal-attachments"
                    >
                      <li
                        v-for="link in notice.attachmentLinks"
                        :key="link.href"
                      >
                        <a
                          :href="link.href"
                          target="_blank"
                          rel="noopener noreferrer"
                          :download="link.downloadName"
                        >{{ link.label }}</a>
                      </li>
                    </ul>
                  </div>
                  <teleport to="body">
                    <div
                      v-if="activeTooltip"
                      class="notice-richtext-tooltip"
                      :style="tooltipPosition"
                      v-html="activeTooltip.content"
                      @click.stop
                    ></div>
                  </teleport>
                </div>
              `,
              data() {
                return {
                  notices: noticesData,
                  activeTooltip: null as { content: string; noticeId: string } | null,
                  tooltipPosition: { top: '0px', left: '0px' },
                }
              },
              mounted() {
                // 外部クリックでツールチップを閉じるイベントリスナーを追加
                document.addEventListener('click', this.handleDocumentClick)
              },
              beforeUnmount() {
                // イベントリスナーを削除
                document.removeEventListener('click', this.handleDocumentClick)
              },
              methods: {
                handleTooltipToggle(event: MouseEvent, notice: any) {
                  const target = event.target as HTMLElement
                  if (
                    target.classList.contains('notice-tooltip-trigger') &&
                    notice.tooltipContentHtml
                  ) {
                    event.stopPropagation()

                    // 同じツールチップをクリックした場合は閉じる
                    if (this.activeTooltip && this.activeTooltip.noticeId === notice.id) {
                      this.activeTooltip = null
                      return
                    }

                    const rect = target.getBoundingClientRect()
                    this.tooltipPosition = {
                      top: `${rect.top + window.scrollY - 10}px`,
                      left: `${rect.left + window.scrollX + rect.width / 2}px`,
                    }
                    this.activeTooltip = {
                      content: notice.tooltipContentHtml,
                      noticeId: notice.id,
                    }
                  }
                },
                handleOutsideClick(event: MouseEvent) {
                  const target = event.target as HTMLElement
                  if (
                    !target.classList.contains('notice-tooltip-trigger') &&
                    !target.closest('.notice-richtext-tooltip')
                  ) {
                    this.activeTooltip = null
                  }
                },
                handleDocumentClick(event: MouseEvent) {
                  const target = event.target as HTMLElement
                  if (
                    !target.closest('.notice-richtext-tooltip') &&
                    !target.classList.contains('notice-tooltip-trigger')
                  ) {
                    this.activeTooltip = null
                  }
                },
              },
            },
          })
        }
      } catch (error) {
        console.error('モーダル表示用お知らせの取得に失敗しました:', error)
        console.error(SERVICE_LEVEL_NOTICE_HIDDEN_PAGES_DIRECTUS_HINT)
      }
    },
  },
}
</script>
<style lang="scss">
// 郵便番号入力枠のスタイル上書き
.yhc-ef-zip-input {
  .yhc-ef-zip-input--title {
    font-size: 20px !important;
    margin-bottom: 0 !important;

    @media screen and (max-width: 757px) {
      font-size: 18px !important;
      margin-bottom: 0 !important;
    }
  }

  .yhc-ef-zip-input--inner {
    padding: var(--yhc-base-pad-lg) !important;

    .result-address-contents {
      .result-address-title {
        // font-size: 14px;
        margin-bottom: 0 !important;
      }

      .result-address-text {
        // font-size: 16px;
        margin-bottom: 0 !important;
        font-weight: 600;
        color: var(--yhc-color);
      }
    }
  }
}

.yhc-service-level {
  &__notice {
    width: 100%;
    margin-bottom: 1rem;

    @media (max-width: 767px) {
      margin-bottom: 0.5rem;
    }
  }
}

// トップセクション：郵便番号入力とお預かり日選択を横並び配置
.yhc-service-level-top-section {
  display: flex;
  justify-content: flex-start; // 左寄せ
  flex-wrap: wrap; // 必要に応じて折り返す
  gap: 20px;
  margin-top: 1rem;

  // スマホ表示時の調整
  @media (max-width: 767px) {
    flex-direction: column; // 縦積み
    gap: 10px;
  }
}

// お知らせモーダルのスタイル
.yhc-notice-modal-content {
  .yhc-notice-modal-item {
    padding: 0.25rem 0;

    // リッチテキストコンテンツのスタイル
    ul {
      margin: 0;
      padding-left: 1em;
      list-style-type: disc;
    }

    p {
      margin-bottom: 0.5rem;
    }

    a {
      color: var(--yhc-color, #007bff);
      text-decoration: underline;
    }
  }

  // お知らせ間の区切り線
  .yhc-notice-modal-separator {
    margin: 0.25rem 0;
    border: 0;
    border-top: 1px solid #7f8285;
  }

  .yhc-notice-modal-attachments {
    margin: 0.25rem 0 0;
    padding-left: 1.2em;
    list-style-type: disc;

    li {
      font-size: 14px;
      line-height: 1.4;
    }

    a {
      color: var(--yhc-color, #007bff);
      text-decoration: underline;
    }
  }
}
</style>
